import { useParams, Link, useNavigate } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { comics } from '@/mocks/comics';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex flex-col items-center justify-center py-32 px-4">
          <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-6">
            <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
          </div>
          <h1 className="text-xl font-bold text-foreground-900 mb-2">漫画未找到</h1>
          <p className="text-sm text-foreground-500 mb-6">该漫画可能已被下架或链接无效</p>
          <Link
            to="/"
            className="px-6 py-2.5 rounded-full bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-800 transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 酷漫屋`,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-15',
    dateModified: comic.updateTime,
    publisher: {
      '@type': 'Organization',
      name: '酷漫屋',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://lst56.com/article/${comic.slug}`,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://lst56.com/' },
      { '@type': 'ListItem', position: 2, name: `${comic.categoryName}漫画`, item: `http://lst56.com/category/${comic.category}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${comic.title} - 酷漫屋 | ${comic.author} | ${comic.categoryName}漫画在线观看`}
        description={`${comic.title}：${comic.description} 作者：${comic.author}，状态：${comic.status}，评分：${comic.rating}。在酷漫屋免费在线阅读。`}
        keywords={`酷漫屋,${comic.title},${comic.author},${comic.categoryName},免费漫画,下拉式漫画`}
        canonicalUrl={`http://lst56.com/article/${comic.slug}`}
        ogType="article"
        jsonLd={articleJsonLd}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          {/* Comic info section */}
          <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #3d2b1f 0%, #2a1a10 40%, #1a0f08 100%)' }}>
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
              <div className="flex flex-col lg:flex-row gap-10 md:gap-16 items-start">
                {/* Left: Cover */}
                <div className="flex-shrink-0 mx-auto lg:mx-0">
                  <div className="w-56 md:w-64 rounded-xl overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                    <img
                      src={comic.cover}
                      alt={comic.title}
                      className="w-full aspect-[3/4] object-cover"
                    />
                  </div>
                </div>

                {/* Right: Info */}
                <div className="flex-1 flex flex-col gap-5">
                  {/* Category badge */}
                  <div>
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: 'rgba(255,180,180,0.2)', color: '#f0c0c0' }}>
                      /{comic.categoryName}/
                    </span>
                  </div>

                  {/* Title */}
                  <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight" style={{ fontFamily: "var(--font-heading)" }}>
                    {comic.title}
                  </h1>

                  {/* Author & meta */}
                  <p className="text-sm md:text-base" style={{ color: 'rgba(255,255,255,0.65)' }}>
                    作者：{comic.author}
                  </p>

                  {/* Description */}
                  <p className="text-sm md:text-base leading-relaxed max-w-2xl" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    {comic.description}
                  </p>

                  {/* Tags */}
                  <div className="flex gap-2 flex-wrap">
                    {comic.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-3 py-1 rounded-full text-xs"
                        style={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Stats */}
                  <div className="flex flex-wrap gap-6 md:gap-10">
                    <div className="flex flex-col">
                      <span className="text-4xl md:text-5xl font-black text-white" style={{ fontFamily: "var(--font-heading)" }}>
                        {comic.chapters}
                      </span>
                      <span className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>总章节数</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-2xl md:text-3xl font-bold text-white flex items-center gap-1">
                        {comic.rating}
                        <i className="ri-star-fill text-accent-500 text-xl w-5 h-5 flex items-center justify-center"></i>
                      </span>
                      <span className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>用户评分</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-2xl md:text-3xl font-bold text-white">
                        {comic.views}
                      </span>
                      <span className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.45)' }}>累计阅读</span>
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-3">
                    <button
                      onClick={() => navigate(`/category/${comic.category}`)}
                      className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-background-50 text-foreground-950 text-sm font-semibold hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
                    >
                      开始阅读
                      <i className="ri-play-circle-line text-lg"></i>
                    </button>
                    <button
                      onClick={() => navigate(`/category/${comic.category}`)}
                      className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer"
                      style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'rgba(255,255,255,0.8)' }}
                    >
                      加入书架
                      <i className="ri-bookmark-line text-base"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.categoryName}漫画`, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          {/* Chapter list */}
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8" style={{ fontFamily: "var(--font-heading)" }}>
                章节<span className="text-accent-500">列表</span>
              </h2>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {Array.from({ length: Math.min(comic.chapters, 20) }).map((_, i) => {
                  const chNum = comic.chapters - i;
                  return (
                    <button
                      key={chNum}
                      onClick={() => navigate(`/category/${comic.category}`)}
                      className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors duration-200 text-sm cursor-pointer"
                    >
                      <span className="text-foreground-700 font-medium whitespace-nowrap">第{chNum}话</span>
                      <span className="text-xs text-foreground-400">
                        {i === 0 ? '最新' : ''}
                      </span>
                    </button>
                  );
                })}
              </div>

              {comic.chapters > 20 && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => navigate(`/category/${comic.category}`)}
                    className="px-6 py-2.5 rounded-full border border-background-300 text-sm text-foreground-600 hover:border-foreground-400 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    查看全部 {comic.chapters} 章
                    <i className="ri-arrow-down-s-line ml-1 text-base"></i>
                  </button>
                </div>
              )}
            </div>
          </section>

          {/* Related comics */}
          {relatedComics.length > 0 && (
            <section className="py-12 md:py-16 bg-background-100">
              <div className="max-w-7xl mx-auto px-4 md:px-6">
                <h2 className="text-xl md:text-2xl font-bold text-foreground-950 mb-8" style={{ fontFamily: "var(--font-heading)" }}>
                  相关<span className="text-accent-500">推荐</span>
                </h2>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to={`/article/${rc.slug}`}
                      className="group cursor-pointer"
                    >
                      <div className="rounded-xl overflow-hidden transition-all duration-300 group-hover:scale-[1.02]">
                        <div className="aspect-[3/4] overflow-hidden">
                          <img
                            src={rc.cover}
                            alt={rc.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      </div>
                      <div className="mt-3 px-1">
                        <h3 className="text-sm font-semibold text-foreground-900 truncate">{rc.title}</h3>
                        <div className="flex items-center gap-1.5 mt-1">
                          <i className="ri-star-fill text-accent-500 text-xs w-3 h-3 flex items-center justify-center"></i>
                          <span className="text-xs text-foreground-600">{rc.rating}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="relative w-full h-[320px] md:h-[380px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=A%20beautiful%20Korean%20webtoon%20panoramic%20illustration%20of%20a%20reading%20scene%2C%20cozy%20warm%20amber%20lighting%2C%20open%20book%20with%20magical%20glow%2C%20Korean%20manhwa%20style%2C%20cinematic%20wide%20composition%2C%20inviting%20atmosphere%2C%20rich%20warm%20tones%2C%20storytelling%20aesthetic%2C%20clean%20composition&width=1800&height=500&seq=article-cta-banner&orientation=landscape"
              alt="继续阅读"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/35 to-black/55"></div>
            <div className="relative z-10 w-full h-full flex flex-col justify-between py-10 md:py-14 px-6 md:px-12 lg:px-20">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: "var(--font-heading)" }}>继续阅读</h2>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
                <button
                  onClick={() => navigate(`/category/${comic.category}`)}
                  className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-background-50 text-foreground-950 text-sm font-semibold hover:bg-background-100 transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  阅读下一章
                  <span className="w-7 h-7 rounded-full bg-foreground-950 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-background-50 text-sm"></i>
                  </span>
                </button>
                <p className="text-sm text-white/70">
                  状态：<strong className="text-white font-semibold">{comic.status}</strong><br />
                  最后更新：{comic.updateTime}
                </p>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}